import { useEffect, useMemo, useRef, useState } from 'react';
import { buscarCodigosPostales, type CodigoPostalDto } from '@lib/services/codigos-postales.service';

interface Props {
  /** CP actual (string). Si está vacío, el combobox arranca limpio. */
  value: string;
  onChange: (cp: string) => void;
  /** Mensaje de error a mostrar debajo. */
  error?: string;
  /** Label visible — incluí el asterisco si querés indicar obligatoriedad. */
  label?: string;
  /** Hint que va debajo cuando no hay error. */
  hint?: string;
  /** Para deshabilitar mientras el form está guardando. */
  disabled?: boolean;
}

/**
 * Combobox de CP con typeahead contra `/codigos-postales/search`. El input
 * acepta tanto número (1640) como nombre de localidad ("San Isidro") — el
 * backend decide qué tipo de match hacer. Al seleccionar un resultado se
 * pisa el `value` con el código.
 *
 * Hace debounce de 200ms para no machacar el endpoint con cada tecla. Usa
 * un AbortController para cancelar requests viejos cuando llega uno nuevo
 * (no terminar con el dropdown mostrando un resultado de hace 3 letras).
 */
export function CodigoPostalCombobox({
  value,
  onChange,
  error,
  label = 'CP',
  hint,
  disabled,
}: Props) {
  // texto que ve el usuario — puede ser un código (1640) o el inicio de un
  // nombre. Cuando elige una opción mostramos "1640 — San Isidro" y guardamos
  // el código real en `value` (via onChange).
  const [text, setText] = useState<string>(value ?? '');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<CodigoPostalDto[]>([]);
  const [loading, setLoading] = useState(false);
  // Un 500 del catálogo se mostraba como "Sin resultados": el usuario asumía
  // que su código postal no existía y cargaba otro.
  const [fallo, setFallo] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Sincronizar text cuando cambia value desde afuera (ej. el padre reset el
  // form). No corremos el effect cuando el cambio salió de adentro.
  useEffect(() => {
    if (value !== text) setText(value ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounce del fetch.
  useEffect(() => {
    if (!text || text.trim().length < 2) {
      setResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        const data = await buscarCodigosPostales(text, 20);
        if (!ctrl.signal.aborted) {
          setResults(data);
          setFallo(false);
        }
      } catch {
        if (!ctrl.signal.aborted) {
          setResults([]);
          setFallo(true);
        }
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [text]);

  // Cerrar el dropdown al clickear afuera.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function handleSelect(cp: CodigoPostalDto) {
    setText(cp.codigo);
    onChange(cp.codigo);
    setOpen(false);
  }

  const showDropdown = open && (loading || results.length > 0 || fallo);
  const placeholderHint = useMemo(
    () => hint ?? 'Escribí el código (ej. 1640) o el nombre de la localidad',
    [hint],
  );

  return (
    <div
      ref={containerRef}
      className="onboarding__field"
      style={{ position: 'relative' }}
    >
      <label className="onboarding__fieldLabel">{label}</label>
      <input
        className="input"
        type="text"
        inputMode="text"
        autoComplete="postal-code"
        value={text}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          onChange(next.trim());
          setOpen(true);
        }}
      />
      {hint && !error && <span className="onboarding__fieldHint">{placeholderHint}</span>}
      {error && <span className="onboarding__fieldError">{error}</span>}

      {showDropdown && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 50,
            margin: 0,
            padding: 4,
            background: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            maxHeight: 260,
            overflowY: 'auto',
            listStyle: 'none',
            fontSize: 13,
          }}
        >
          {loading && (
            <li style={{ padding: '8px 10px', color: '#6b7280' }}>Buscando…</li>
          )}
          {!loading && results.length === 0 && (
            <li style={{ padding: '8px 10px', color: fallo ? 'var(--color-error-dark)' : '#6b7280' }}>
              {fallo ? 'No se pudo buscar el código postal. Probá de nuevo.' : 'Sin resultados'}
            </li>
          )}
          {!loading &&
            results.map((r) => (
              <li key={r.codigo}>
                <button
                  type="button"
                  onClick={() => handleSelect(r)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '6px 10px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{r.codigo}</strong>{' '}
                  — {r.localidad}{' '}
                  <span style={{ color: '#6b7280', fontSize: 12 }}>({r.provincia})</span>
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
